import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { Loader2, LogOut, Zap, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge, VT_LABELS } from "@/components/TicketBits";

const STATUS_LABELS = {
  pending_payment: "In attesa pagamento",
  paid: "Pagato",
  confirmed: "Confermato",
  completed: "Completato",
  cancelled: "Annullato",
  new: "Nuova",
  read: "Letta",
  archived: "Archiviata",
  programmato: "Programmato",
  completato: "Completato",
  annullato: "Annullato",
};

const TICKET_STATUSES = ["pending_payment", "open", "assigned", "in_progress", "completed", "closed", "cancelled"];
const APPT_STATUSES = ["programmato", "completato", "annullato"];

const Badge = ({ status }) => {
  const colors = {
    paid: "text-emerald-400 border-emerald-400/30",
    confirmed: "text-volt border-volt/30",
    completed: "text-slate-400 border-white/15",
    cancelled: "text-red-400 border-red-400/30",
    pending_payment: "text-spark border-spark/30",
    new: "text-spark border-spark/30",
    read: "text-volt border-volt/30",
    archived: "text-slate-500 border-white/10",
    programmato: "text-volt border-volt/30",
    completato: "text-emerald-400 border-emerald-400/30",
    annullato: "text-red-400 border-red-400/30",
  };
  return (
    <span className={`font-mono text-[10px] uppercase tracking-wider border rounded-full px-2.5 py-1 ${colors[status] || "text-slate-400 border-white/15"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const inputCls =
  "bg-obsidian border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-volt";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("vv_token"));
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("tickets");
  const [tickets, setTickets] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [appt, setAppt] = useState({}); // lead_id -> {date, time}

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [t, b, l, a] = await Promise.all([
        api.get("/admin/tickets", { headers }),
        api.get("/admin/bookings", { headers }),
        api.get("/admin/leads", { headers }),
        api.get("/admin/appointments", { headers }),
      ]);
      setTickets(t.data);
      setBookings(b.data);
      setLeads(l.data);
      setAppointments(a.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("vv_token");
        setToken(null);
      }
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      if (data.user.role !== "admin") {
        setError("Questo account non è amministratore");
        return;
      }
      localStorage.setItem("vv_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (kind, id, status) => {
    try {
      await api.patch(`/admin/${kind}/${id}`, { status }, { headers });
      load();
    } catch {
      /* stato non aggiornato */
    }
  };

  const updateTicketStatus = async (id, status) => {
    try {
      await api.patch(`/tickets/${id}/status`, { status }, { headers });
      load();
    } catch {
      /* stato non aggiornato */
    }
  };

  const fissaAppuntamento = async (leadId) => {
    const a = appt[leadId];
    if (!a?.date || !a?.time) return toast.error("Scegli data e ora dell'appuntamento");
    try {
      await api.post("/admin/appointments", { lead_id: leadId, date: a.date, time: a.time }, { headers });
      toast.success("Appuntamento fissato — email di conferma inviata al cliente");
      setAppt((p) => ({ ...p, [leadId]: {} }));
      load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Errore");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-obsidian bg-grid flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="glass-card rounded-2xl p-8 w-full max-w-sm" data-testid="admin-login-form">
          <div className="flex justify-center mb-6">
            <Logo />
          </div>
          <p className="eyebrow text-center mb-6">Console Riservata</p>
          <input
            data-testid="admin-email-input"
            type="email"
            placeholder="Email admin"
            value={loginForm.email}
            onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full bg-obsidian/60 border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-volt mb-3"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            placeholder="Password"
            value={loginForm.password}
            onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
            className="w-full bg-obsidian/60 border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-volt mb-4"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-red-400 text-xs mb-3 font-mono">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold py-3 rounded-full hover:bg-spark transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />} Accedi
          </button>
          <a href="/" data-testid="admin-back-home-link" className="block text-center mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-600 hover:text-volt transition-colors">
            ← Torna al sito
          </a>
        </form>
      </div>
    );
  }

  const TabBtn = ({ id, label, count }) => (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => setTab(id)}
      className={`font-mono text-xs uppercase tracking-widest px-4 sm:px-5 py-2.5 rounded-full border transition-colors ${
        tab === id ? "border-volt text-volt bg-volt/10" : "border-white/10 text-slate-500 hover:text-slate-300"
      }`}
    >
      {label} ({count})
    </button>
  );

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <header className="border-b border-white/5 backdrop-blur-md bg-slate-950/80 sticky top-0 z-40">
        <div className="px-4 sm:px-8 max-w-6xl mx-auto h-16 flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site-link" className="font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-volt transition-colors">
              Vedi sito
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                localStorage.removeItem("vv_token");
                setToken(null);
              }}
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-red-400 border border-white/10 hover:border-red-400/40 rounded-full px-4 py-2 transition-colors"
            >
              <LogOut size={12} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-8 max-w-6xl mx-auto py-10">
        <div className="flex gap-2 sm:gap-3 mb-8 flex-wrap">
          <TabBtn id="tickets" label="Ticket" count={tickets.length} />
          <TabBtn id="agenda" label="Agenda" count={appointments.length} />
          <TabBtn id="leads" label="Richieste" count={leads.length} />
          <TabBtn id="bookings" label="Prenotazioni" count={bookings.length} />
        </div>

        {tab === "tickets" && (
          <div className="space-y-3" data-testid="admin-tickets-list">
            {tickets.length === 0 && <p className="text-slate-600 font-mono text-sm">Nessun ticket.</p>}
            {tickets.map((t) => (
              <div key={t.ticket_id} data-testid={`admin-ticket-${t.ticket_id}`} className="glass-card rounded-xl p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display font-bold text-white">{t.title}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="font-mono text-xs text-slate-500 mt-1.5">
                    {t.cliente_name} · {VT_LABELS[t.vehicle_type]} · {t.level_label} €{t.price_eur} (fee €{(t.price_eur * (t.commission_rate ?? 0.2)).toFixed(2)})
                    {t.expert_name && ` · Esperto: ${t.expert_name}`}
                  </p>
                  {t.dtc_codes && <p className="font-mono text-xs text-spark mt-1">DTC: {t.dtc_codes}</p>}
                </div>
                <select
                  data-testid={`admin-ticket-status-${t.ticket_id}`}
                  value={t.status}
                  onChange={(e) => updateTicketStatus(t.ticket_id, e.target.value)}
                  className={inputCls}
                >
                  {TICKET_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "agenda" && (
          <div className="space-y-3" data-testid="admin-appointments-list">
            {appointments.length === 0 && (
              <p className="text-slate-600 font-mono text-sm">
                Nessun appuntamento. Fissali dalla tab Richieste sui preventivi accettati.
              </p>
            )}
            {appointments.map((a) => (
              <div key={a.appointment_id} data-testid={`admin-appt-${a.appointment_id}`} className="glass-card rounded-xl p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <CalendarCheck size={16} className="text-volt" />
                    <span className="font-display font-bold text-white">{a.name}</span>
                    <Badge status={a.status} />
                  </div>
                  <p className="font-mono text-xs text-slate-500 mt-1.5">
                    {a.date} ore {a.time} {a.phone && `· ${a.phone}`} {a.email && `· ${a.email}`} {a.vehicle && `· ${a.vehicle}`}
                  </p>
                  {a.notes && <p className="text-xs text-slate-500 mt-1 italic">{a.notes}</p>}
                </div>
                <select
                  data-testid={`admin-appt-status-${a.appointment_id}`}
                  value={a.status}
                  onChange={(e) => updateStatus("appointments", a.appointment_id, e.target.value)}
                  className={inputCls}
                >
                  {APPT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-3" data-testid="admin-leads-list">
            {leads.length === 0 && <p className="text-slate-600 font-mono text-sm">Nessuna richiesta.</p>}
            {leads.map((l) => {
              const isPrev = l.message?.startsWith("PREVENTIVO AI ACCETTATO");
              return (
                <div key={l.lead_id} data-testid={`admin-lead-${l.lead_id}`} className="glass-card rounded-xl p-5 space-y-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-display font-bold text-white">{l.name}</span>
                        <Badge status={l.status} />
                        {isPrev && (
                          <span className="font-mono text-[10px] uppercase tracking-wider border border-emerald-400/40 text-emerald-400 rounded-full px-2.5 py-1">
                            Preventivo accettato
                          </span>
                        )}
                      </div>
                      <p className="font-mono text-xs text-slate-500 mt-1.5">
                        {l.email || "—"} {l.phone && `· ${l.phone}`} {l.vehicle && `· ${l.vehicle}`}
                      </p>
                      <p className="text-sm text-slate-400 mt-2 whitespace-pre-wrap line-clamp-4">{l.message}</p>
                    </div>
                    <select
                      data-testid={`admin-lead-status-${l.lead_id}`}
                      value={l.status}
                      onChange={(e) => updateStatus("leads", l.lead_id, e.target.value)}
                      className={inputCls}
                    >
                      {["new", "read", "archived"].map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 border-t border-white/5 pt-4">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                      <CalendarCheck size={12} className="text-volt" /> Fissa appuntamento:
                    </span>
                    <input
                      data-testid={`admin-lead-appt-date-${l.lead_id}`}
                      type="date"
                      value={appt[l.lead_id]?.date || ""}
                      onChange={(e) => setAppt((p) => ({ ...p, [l.lead_id]: { ...p[l.lead_id], date: e.target.value } }))}
                      className={inputCls}
                    />
                    <input
                      data-testid={`admin-lead-appt-time-${l.lead_id}`}
                      type="time"
                      value={appt[l.lead_id]?.time || ""}
                      onChange={(e) => setAppt((p) => ({ ...p, [l.lead_id]: { ...p[l.lead_id], time: e.target.value } }))}
                      className={inputCls}
                    />
                    <button
                      data-testid={`admin-lead-appt-submit-${l.lead_id}`}
                      onClick={() => fissaAppuntamento(l.lead_id)}
                      className="bg-volt text-obsidian font-semibold text-xs px-4 py-2 rounded-full hover:bg-spark transition-colors"
                    >
                      Fissa
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "bookings" && (
          <div className="space-y-3" data-testid="admin-bookings-list">
            {bookings.length === 0 && <p className="text-slate-600 font-mono text-sm">Nessuna prenotazione.</p>}
            {bookings.map((b) => (
              <div key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`} className="glass-card rounded-xl p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display font-bold text-white">{b.name}</span>
                    <Badge status={b.status} />
                  </div>
                  <p className="font-mono text-xs text-slate-500 mt-1.5">
                    {b.date} · {b.time} · {b.connection === "teamviewer" ? "TeamViewer" : "Videochiamata"} · {b.email} · {b.phone}
                  </p>
                  {b.vehicle && <p className="text-xs text-slate-400 mt-1">Veicolo: {b.vehicle}</p>}
                </div>
                <select
                  data-testid={`admin-booking-status-${b.booking_id}`}
                  value={b.status}
                  onChange={(e) => updateStatus("bookings", b.booking_id, e.target.value)}
                  className={inputCls}
                >
                  {["pending_payment", "paid", "confirmed", "completed", "cancelled"].map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
